import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Zap } from "lucide-react";

import { useMyApplications } from "@/features/applications/model/useApplications";
import type { ApplicationStatus } from "@/features/applications/types";
import { useCalendar } from "@/features/availability/model/useAvailability";
import { useShiftFeedStore } from "@/features/shifts/model/shiftFeedStore";
import { useOpenShifts } from "@/features/shifts/model/useShifts";
import { ActiveFilters } from "@/features/shifts/ui/ActiveFilters";
import { ShiftCard } from "@/features/shifts/ui/ShiftCard";
import { ShiftFilterBar } from "@/features/shifts/ui/ShiftFilterBar";
import type { ShiftFilters } from "@/features/shifts/types";

export function ShiftFeed() {
  const { data: calendar } = useCalendar();
  const calendarInitialized = calendar?.initialized ?? false;

  const [filters, setFilters] = useState<ShiftFilters>({});
  // Strictly user-controlled. Defaults off so a fresh worker — or a worker
  // who set up a calendar without realizing it filters the feed — always
  // sees the full list until they opt in. Persists per-browser so a
  // deliberate toggle survives reloads, and is reset on logout / persona
  // switch to keep state from leaking across accounts.
  const availableOnly = useShiftFeedStore((s) => s.availableOnly);
  const setAvailableOnly = useShiftFeedStore((s) => s.setAvailableOnly);
  const [urgentOnly, setUrgentOnly] = useState(false);

  const effectiveFilters: ShiftFilters = useMemo(
    () => ({
      ...filters,
      available_only: availableOnly ? true : undefined,
      urgent_only: urgentOnly ? true : undefined,
    }),
    [filters, availableOnly, urgentOnly],
  );

  const { data, isLoading, error } = useOpenShifts(effectiveFilters);
  const { data: myApps } = useMyApplications();

  const appByShift = useMemo(() => {
    const map = new Map<string, ApplicationStatus>();
    myApps?.forEach((a) => map.set(a.shift_id, a.status));
    return map;
  }, [myApps]);

  return (
    <div className="space-y-4">
      {calendarInitialized ? (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface px-4 py-2.5 text-sm shadow-card">
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={availableOnly}
              onChange={(e) => setAvailableOnly(e.target.checked)}
              className="h-4 w-4 accent-primary"
            />
            <span className="font-medium text-copy">Available shifts only</span>
            <span className="text-xs text-copy-muted">— match my availability calendar</span>
          </label>
          <Link
            to="/worker/availability"
            className="text-xs font-medium text-primary hover:underline"
          >
            Edit availability
          </Link>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-surface px-4 py-2.5 text-sm shadow-card">
          <span className="text-copy-muted">
            Set your availability to filter shifts that fit your schedule.
          </span>
          <Link
            to="/worker/availability"
            className="text-xs font-medium text-primary hover:underline"
          >
            Set availability
          </Link>
        </div>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={() => setUrgentOnly((v) => !v)}
          className={
            "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors " +
            (urgentOnly
              ? "border-destructive bg-destructive text-primary-foreground"
              : "border-border bg-surface text-copy-muted hover:bg-surface-sunken")
          }
          aria-pressed={urgentOnly}
        >
          <Zap className="h-3 w-3" />
          Urgent only
        </button>
      </div>

      <ShiftFilterBar filters={filters} onChange={setFilters} />
      <ActiveFilters filters={filters} onChange={setFilters} />

      {isLoading && <div className="text-sm text-copy-muted">Loading shifts…</div>}
      {error && <div className="text-sm text-destructive">Could not load shifts.</div>}

      {data && data.shifts.length === 0 && (
        <div className="rounded-lg border border-border bg-surface p-10 text-center shadow-card">
          <p className="text-sm font-medium text-copy">No shifts match your filters.</p>
          <div className="mt-2 flex flex-wrap items-center justify-center gap-3 text-xs">
            {availableOnly && calendarInitialized && (
              <button
                type="button"
                onClick={() => setAvailableOnly(false)}
                className="font-medium text-primary hover:underline"
              >
                Show shifts that don't match my availability
              </button>
            )}
            {Object.keys(filters).length > 0 && (
              <button
                type="button"
                onClick={() => setFilters({})}
                className="font-medium text-primary hover:underline"
              >
                Clear filters
              </button>
            )}
          </div>
        </div>
      )}

      {data && data.shifts.length > 0 && (
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          {data.shifts.map((shift) => (
            <ShiftCard
              key={shift.id}
              shift={shift}
              to={`/worker/shifts/${shift.id}`}
              myApplicationStatus={appByShift.get(shift.id)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
